// Condition Node Component

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  QuestionMark as QuestionIcon,
  CheckCircle as TrueIcon,
  Cancel as FalseIcon,
  Settings as ConditionIcon
} from '@mui/icons-material';

interface ConditionNodeData {
  label: string;
  description?: string;
  condition?: {
    field: string;
    operator: string;
    value: any;
  };
  status?: 'idle' | 'running' | 'true' | 'false';
  lastResult?: boolean;
}

const ConditionNode: React.FC<NodeProps<ConditionNodeData>> = ({ 
  data, 
  selected, 
  id 
}) => {
  const getStatusIcon = () => {
    switch (data.status) {
      case 'true':
        return <TrueIcon sx={{ color: '#4CAF50' }} />;
      case 'false':
        return <FalseIcon sx={{ color: '#F44336' }} />;
      case 'running':
        return <ConditionIcon sx={{ color: '#2196F3', animation: 'spin 1s linear infinite' }} />;
      default:
        return <QuestionIcon sx={{ color: '#FF9800' }} />;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'true':
        return '#E8F5E9';
      case 'false':
        return '#FFEBEE';
      case 'running':
        return '#E3F2FD';
      default:
        return '#FFF3E0';
    }
  };

  const getBorderColor = () => {
    if (selected) return '#1976D2';
    switch (data.status) {
      case 'true':
        return '#4CAF50';
      case 'false':
        return '#F44336';
      case 'running':
        return '#2196F3';
      default:
        return '#FF9800';
    }
  };

  const formatCondition = (condition: any) => {
    if (!condition) return 'No condition set';
    return `${condition.field} ${condition.operator} ${condition.value}`;
  };

  return (
    <Box sx={{ minWidth: 200 }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />
      
      {/* Diamond-shaped container */}
      <Box
        sx={{
          position: 'relative',
          width: 140,
          height: 140,
          transform: 'rotate(45deg)',
          backgroundColor: getStatusColor(),
          border: `3px solid ${getBorderColor()}`,
          borderRadius: 2,
          transition: 'all 0.2s ease',
          boxShadow: selected ? 4 : 2,
          '&:hover': {
            boxShadow: 6,
            transform: 'rotate(45deg) scale(1.05)'
          }
        }}
      >
        {/* Content container (rotated back) */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            width: '80%',
            height: '80%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}
        >
          {/* Icon */}
          <Box sx={{ mb: 1 }}>
            {getStatusIcon()}
          </Box>

          {/* Label */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              lineHeight: 1,
              mb: 0.5
            }}
          >
            {data.label}
          </Typography>

          {/* Condition display */}
          {data.condition && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                color: 'text.secondary',
                lineHeight: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {formatCondition(data.condition)}
            </Typography>
          )}

          {/* Result indicator */}
          {data.lastResult !== undefined && (
            <Chip
              label={data.lastResult ? 'TRUE' : 'FALSE'}
              size="small"
              sx={{
                mt: 0.5,
                height: 16,
                fontSize: '0.6rem',
                backgroundColor: data.lastResult ? '#4CAF50' : '#F44336',
                color: 'white',
                '& .MuiChip-label': {
                  px: 1
                }
              }}
            />
          )}
        </Box>
      </Box>

      {/* Output Handles */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={{
          background: '#4CAF50',
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%',
          left: '30%'
        }}
      />
      
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{
          background: '#F44336',
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%',
          left: '70%'
        }}
      />

      {/* Labels for outputs */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -25,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          px: 2
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: '#4CAF50',
            fontWeight: 600
          }}
        >
          TRUE
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: '#F44336',
            fontWeight: 600
          }}
        >
          FALSE
        </Typography>
      </Box>

      {/* Running indicator */}
      {data.status === 'running' && (
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#2196F3',
            animation: 'pulse 1.5s ease-in-out infinite',
            zIndex: 10
          }}
        />
      )}

      {/* Add spinning animation keyframes */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.3);
              opacity: 0.7;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(ConditionNode);